
import { Component, Prop, Mixins, Provide } from "vue-property-decorator";
import { FilteringSortingTableStore } from "@/mixins/tableWithFilteringAndSortingStore";
import Context from "@/mixins/context";
import { DataModules } from "@/store/modules/data";
import { IFilter, ISorter } from "@/models/tables";
import { ITaxTag, ITaxTemplate } from "@/models/tax";
import { CreateElement } from "vue";
import { DefaultTaxTagFilters } from "@/data/filters/tax/tags";
import { DefaultTaxTagSorters } from "@/data/sorters/tax/tags";
import {
  ITaxTagsProvider,
  ITaxTagsProviderMethods
} from "@/models/providers/tax";
import { ApiOperators } from "@/data/table";
import { ConfirmModalModes } from "@/data/enums";

@Component
export default class TaxTagsProvider extends Mixins(
  FilteringSortingTableStore,
  Context
) {
  // Props

  @Prop({ default: "TTGP" /* Tax TAGS Provider */ })
  public declare id: string;

  @Prop({ default: "" })
  public declare taxTemplateId: ITaxTemplate["id"];

  @Prop({ default: true })
  public declare handleLoading: boolean;

  @Prop({ default: true })
  public declare handleRequestError: boolean;

  @Prop({ default: () => ({}) })
  public declare manualFilters: Record<string, string | number | boolean>;

  @Prop({ default: () => DefaultTaxTagFilters })
  public declare availableFilters: (vm: TaxTagsProvider) => IFilter[];

  @Prop({ default: () => DefaultTaxTagSorters })
  public declare availableSorters: (vm: TaxTagsProvider) => ISorter[];

  // Data
  public dataModule: DataModules = DataModules.TAX_TAGS;
  public debounceSearch = this.$_.debounce(this.doQuickSearch, 250);

  public isReloading = false;
  public filters: IFilter[] = [];
  public sorters: ISorter[] = [];
  public searchQuery = "";

  // Lifecycle methods
  public async created() {
    this.filters = this.availableFilters(this);
    this.sorters = this.availableSorters(this);
    this.loadData();
  }

  // Getters

  public get hasTaxTags() {
    return !!this.table.data.length;
  }

  public get queryFilter() {
    return this.searchQuery
      ? { [`filter[name|${ApiOperators.LIKE}]`]: `%${this.searchQuery}%` }
      : {};
  }

  public get taxTemplateFilter() {
    return this.taxTemplateId
      ? { [`filter[templates.id]`]: this.taxTemplateId }
      : {};
  }

  // Methods
  public async loadData() {
    try {
      await this.getData({ ignoreStored: this.isReloading });
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isReloading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    this.cancelRequests();
    return this.loadData();
  }

  public doQuickSearch(value = "") {
    if (value === this.searchQuery) return Promise.resolve();
    this.searchQuery = value;
    return this.reloadData();
  }

  public async openAddEditModal(taxTag: ITaxTag) {
    await this.$store.dispatch(
      `data/${DataModules.TAX_TAGS}/openAddEditModal`,
      {
        props: {
          taxTagId: taxTag.id
        },
        events: {
          success: () => {
            this.reloadData();
          }
        }
      }
    );
  }

  public async syncTags(taxTagIds: ITaxTag["id"][]) {
    try {
      this.isReloading = true;
      await this.$store.dispatch(`data/${DataModules.TAX_TEMPLATES}/sync`, {
        taxTemplateId: this.taxTemplateId,
        data: {
          tax_template_id: this.taxTemplateId,
          tax_tag_ids: taxTagIds
        }
      });
    } finally {
      this.reloadData();
    }
  }

  public async deleteTaxTag(taxTag: ITaxTag) {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise<boolean>(async (resolve, reject) => {
      const confirm = await this.$store.dispatch("ui/open/confirmModal", {
        config: {
          props: {
            autoClose: false,
            mode: ConfirmModalModes.BUTTON,
            confirmButtonText: this.$t("_action.delete"),
            message: this.$t("_sentence.delete_confirmation", {
              object: (this.$t("_.tax_tag") as string).toLowerCase()
            })
          },
          events: {
            confirmed: async () => {
              this.deleteTaxTagConfirmed(taxTag)
                .then(() => {
                  resolve(true);
                })
                .catch(() => reject(false))
                .finally(() => {
                  confirm.close();
                });
            }
          }
        }
      });
    });
  }

  public deleteTaxTagConfirmed(taxTag: ITaxTag) {
    return this.$store
      .dispatch(`data/${this.dataModule}/remove`, { id: taxTag.id })
      .then(() => {
        this.getData({ ignoreStored: true });
      })
      .catch(error => {
        this.$store.dispatch("api/handleValidationError", {
          error,
          vm: this
        });
        throw error;
      });
  }

  // Provide

  @Provide("taxTagsProviderMethods")
  public get providerMethods(): ITaxTagsProviderMethods {
    return {
      getData: this.getData,
      doQuickSearch: this.doQuickSearch,
      onFiltersChange: this.onFiltersChange,
      onLimitChange: this.onLimitChange,
      onPageChange: this.onPageChange,
      onSort: this.onSort,
      openAddEditModal: this.openAddEditModal,
      deleteTaxTag: this.deleteTaxTag,
      reloadData: this.reloadData,
      syncTags: this.syncTags,
      debounceSearch: this.debounceSearch
    };
  }

  @Provide("taxTagsProvider")
  public providerData(): ITaxTagsProvider {
    return {
      availableFilters: this.filters,
      availableSorters: this.sorters,
      constQueryFilters: this.constQueryFilters,
      activeFilters: this.activeFilters,
      activeSort: this.table.sort,
      enableFiltering: this.enableFiltering,
      enableSorting: this.enableSorting,
      hasTaxTags: this.hasTaxTags,
      isLoading: this.table.loading,
      isReloading: this.isReloading,
      limit: this.table.limit,
      page: this.table.page,
      taxTags: this.table.data as ITaxTag[],
      searchQuery: this.searchQuery,
      table: this.table,
      total: this.table.total
    };
  }

  // Override mixin method
  public getStoreParams(params: any): Promise<any> {
    return this.$_.merge(
      {
        params: {
          with: "locations",
          ...this.queryFilter,
          ...this.taxTemplateFilter
        }
      },
      params // Important – must merge last
    );
  }

  public render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.loading)
      return h("is-loading", { props: { if: true } });
    // Handle request error
    if (this.handleRequestError && this.requestError)
      return h("request-error", {
        props: { isLoading: this.table.loading && this.requestError },
        on: { click: () => this.getData() }
      });
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $taxTagsData: this.providerData(),
        $taxTagsMethods: this.providerMethods
      })
    );
  }
}
